import React, { useState, useRef, useEffect } from 'react';
import { TextInput, Box, MuiPopover, NumericInput } from 'design-system';
import { Section } from 'react-scale/containers/corp/backfills/batch/components/Section';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JSONEditable from '../LidarliteLintingPage/Components/JSONEditable';
import { Button } from 'design-system/Button';
import useToggle from 'react-use-v13/lib/useToggle';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  evaluationContexts,
  lidarStepNames,
  LidarLinterSection,
  LinterDocsType,
  UPDATE_LINTER_CONFIG_DOCS_URL,
  IODataTypeToDataType,
  ContextDescriptions,
  LidarLinterConfigFields,
  LidarLinterConfigWrapper,
  lidarLinterConfigTypes,
  LidarLinterConfigTypes,
} from '@scale/shared/types/corp/linterConfigDocs';
import {
  LinterArguments,
  LintExamples,
  styles,
  LinterJsonViewer,
} from './LinterConfigDocsComponents';
import { set, map } from 'lodash';
import {
  StepName,
  StepDefinitions,
} from '@scale/shared/lidar/linting/linterDefinition/stepDefinitions';
import { EvaluationContext } from '@scale/shared/lidar/linting/blocks';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import { IODataType } from '@scale/shared/lidar/linting/linterDefinition/basicDefinitions';
import SAlert from 'react-s-alert';

const LinterLidarConfig = ({
  linter,
  linterDocsJson,
  selected = true,
  isPreview = false,
  onLinterUpdate,
  getStyle,
}: {
  linter: LidarLinterSection;
  linterDocsJson: LinterDocsType;
  selected: boolean;
  isPreview?: boolean;
  onLinterUpdate: () => void;
  getStyle: (selected: boolean) => Record<string, string>;
}) => {
  const classes = styles();
  const [isEdit, toggleIsEdit] = useToggle(false);
  const linterRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (selected) {
      linterRef?.current?.scrollIntoView();
    }
  }, []);
  const { input, output } = StepDefinitions[linter.stepName as StepName];
  return (
    <div
      id={`${linter.name}`}
      style={{ maxWidth: 1000, width: '90%', ...getStyle(selected) }}
      ref={linterRef}
    >
      <Section
        name={
          <div style={{ display: 'inline-block' }}>
            <span>{linter.name}</span>
            <div style={{ display: 'inline', padding: '4px 12px', marginLeft: '6px' }}></div>
            <FontAwesomeIcon
              icon="edit"
              size="xs"
              onClick={event => {
                if (!isPreview) {
                  event.stopPropagation();
                  toggleIsEdit();
                }
              }}
            />
          </div>
        }
        forceHidden={!(selected || isEdit)}
        linterSelected={selected}
        sectionId={linter.name.replaceAll(' ', '%20')}
      >
        {!isEdit ? (
          <div>
            <div className={classes.linterSectionBody}>
              <SanitizedMarkdown text={`**Description:** ${linter.description}`} />
              <div style={{ width: 220, marginTop: 60, marginBottom: 60 }}>
                <LinterWizardPopover currentLinter={linter} />
              </div>
              <div style={{ backgroundColor: '#fafaff' }}>
                <Section forceHidden={true} name={`Technical Review`} enableLink={false}>
                  <div>
                    {linter.technicalDescription && (
                      <SanitizedMarkdown text={`**Description:** ${linter.technicalDescription}`} />
                    )}
                    <b>Evaluation Context:</b>{' '}
                    <span style={{ fontStyle: 'italic' }}>&nbsp;{linter.context}&nbsp;</span>
                    <span>{`(${ContextDescriptions[linter.context as EvaluationContext]})`}</span>
                    <br></br>
                    <br></br>
                    <LinterIO input={input} output={output} />
                    <h3>Step Arguments: </h3>
                    <LinterArguments
                      name={linter.stepName ?? ''}
                      type={'string'}
                      description={'Step Name (the first argument)'}
                      defaultValue={''}
                      backgroundColor={'#fff'}
                    />
                    {linter.arguments.map((arg, ix) => (
                      <LinterArguments
                        key={ix}
                        name={arg.name}
                        type={arg.type}
                        defaultValue={arg.default}
                        description={arg.description}
                        backgroundColor={'#fff'}
                      />
                    ))}
                    <div className={classes.linterSectionBody}>
                      <div>
                        <h3>Example Linters</h3>
                        {linter.examples.length > 0 && <LintExamples examples={linter.examples} />}
                      </div>
                    </div>
                  </div>
                </Section>
              </div>
            </div>
          </div>
        ) : (
          <AddLidarLinter
            linterDocsJson={linterDocsJson}
            currentLinter={linter}
            isEdit={true}
            onLinterUpdate={onLinterUpdate}
          />
        )}
      </Section>
    </div>
  );
};

const LinterIO = (props: { input: IODataType; output: IODataType }) => {
  const { input, output } = props;
  return (
    <div>
      <b>Cuboid Mode: </b>
      <span style={{ fontStyle: 'italic' }}>&nbsp;{IODataTypeToDataType[input]}&nbsp;</span>
      <br></br>
      <br></br>
      <b>Assertion Output Type: </b>
      <span style={{ fontStyle: 'italic' }}>&nbsp;{IODataTypeToDataType[output]}&nbsp;</span>
    </div>
  );
};

const NewLinterArguments = (props: {
  newLinterConfig: LidarLinterSection;
  newSetValue: (path: string, newValue: any) => void;
}) => {
  const { newLinterConfig, newSetValue } = props;
  const classes = styles();
  return (
    <>
      {newLinterConfig.arguments.map((argObject, ix) => (
        <div key={ix} style={{ padding: '10px 0px' }}>
          <div className={classes.tripleColumnWrapper} style={{ height: 60 }}>
            <TextInput
              className={classes.tripleColumn}
              label="Field Name"
              value={argObject.name}
              onChange={e => newSetValue(`arguments[${ix}].name`, e.target.value)}
              size="large"
              fullWidth
            />
            <TextInput
              className={classes.tripleColumn}
              label="Field Type (`string`, `number`, `array`)"
              value={argObject.type}
              // onChange={e => newSetValue(`arguments[${ix}].type`, e.target.value)}
              size="large"
              fullWidth
            />
            <TextInput
              className={classes.tripleColumn}
              label="Default Value (Optional)"
              value={argObject.default ?? ''}
              onChange={e => newSetValue(`arguments[${ix}].default`, e.target.value)}
              size="large"
              fullWidth
            />
          </div>
          <div className={classes.doubleColumnWrapper}>
            <div className={classes.widerColumn}>
              <TextInput
                label="Description"
                value={argObject.description}
                onChange={e => newSetValue(`arguments[${ix}].description`, e.target.value)}
                size="large"
                fullWidth
              />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export const AddLidarLinter = (props: {
  linterDocsJson: LinterDocsType;
  currentLinter: LidarLinterSection;
  isEdit?: boolean;
  onLinterUpdate: () => void;
  popupClose?: () => void;
}) => {
  const { linterDocsJson, currentLinter, onLinterUpdate, popupClose, isEdit = false } = props;
  const [newLinterConfig, setNewLinterConfig] = useState<LidarLinterSection>({
    ...currentLinter,
  });
  const [linterInput, setLinterInput] = useState<any>(
    currentLinter.stepName ? StepDefinitions[currentLinter.stepName as StepName].input : undefined,
  );
  const [linterOutput, setLinterOutput] = useState<any>(
    currentLinter.stepName ? StepDefinitions[currentLinter.stepName as StepName].output : undefined,
  );

  const currentLinterName = `${currentLinter.name}`;
  const onStepNameSelect = (stepName: string) => {
    const { input, output, blockArgTypes } = StepDefinitions[stepName as StepName];
    setLinterInput(input);
    setLinterOutput(output);
    const linterArguments = blockArgTypes.map(argType => {
      return {
        type: argType,
        description: '',
        default: '',
        name: '',
      };
    });
    setNewLinterConfig({
      ...newLinterConfig,
      stepName,
      arguments: linterArguments,
    });
  };
  const editConfigName = (
    fieldName: LidarLinterConfigFields,
    newValue: LidarLinterConfigFields,
  ): void => {
    if (fieldName !== newValue && newLinterConfig.config[fieldName]) {
      const keys = Object.keys(newLinterConfig.config) as LidarLinterConfigFields[];
      const configCopy = keys.reduce((acc: LidarLinterConfigWrapper, oldName) => {
        if (oldName === fieldName) {
          acc[newValue] = newLinterConfig.config[oldName];
        } else {
          acc[oldName] = newLinterConfig.config[oldName];
        }
        return acc;
      }, {});
      newSetValue('config', configCopy);
    }
  };
  const newSetValue = (path: string, newValue: any): void => {
    let configCopy = { ...newLinterConfig };
    configCopy = set(configCopy, path, newValue);
    setNewLinterConfig(configCopy);
  };

  const setLinterName = (linterName: string) => {
    if (
      linterDocsJson.lidarLinters.find(
        linter => linter.name.toLocaleLowerCase() === linterName.toLocaleLowerCase(),
      )
    ) {
      SAlert.error(`Lidar Linter "${linterName}" already exists`, {
        timeout: 'none',
      });
    } else {
      newSetValue('name', linterName);
    }
  };

  const addNewConfigExample = () => {
    const examplesCopy = [...newLinterConfig.examples];
    examplesCopy.push({ name: '', configJson: {} });
    newSetValue('examples', examplesCopy);
  };

  const addNewConfigArg = () => {
    const configCopy = { ...newLinterConfig.config };
    configCopy['Config Name'] = {
      type: '',
      description: '',
      path: '',
    };
    newSetValue('config', configCopy);
  };

  const deleteConfigExample = (index: number): void => {
    const examplesCopy = [...newLinterConfig.examples];
    examplesCopy.splice(index, 1);
    newSetValue('examples', examplesCopy);
  };
  const deleteConfigArg = (fieldName: string): void => {
    const configCopy = { ...newLinterConfig.config };
    delete configCopy[fieldName as LidarLinterConfigFields];
    newSetValue('config', configCopy);
  };
  const classes = styles();
  const saveChanges = async () => {
    if (!newLinterConfig.examples.length) {
      SAlert.error(`Complete an example before saving`, {
        timeout: 'none',
      });
    } else {
      const updatedJson = { ...linterDocsJson };
      const existingLinterIndex = linterDocsJson.lidarLinters.findIndex(
        linterObj => linterObj.name === newLinterConfig.name,
      );
      if (existingLinterIndex >= 0) {
        updatedJson.lidarLinters[existingLinterIndex] = newLinterConfig;
      } else if (isEdit) {
        const oldLinterIndex = linterDocsJson.lidarLinters.findIndex(
          linterObj => linterObj.name === currentLinterName,
        );
        updatedJson.lidarLinters[oldLinterIndex] = newLinterConfig;
      } else {
        updatedJson.lidarLinters.push(newLinterConfig);
      }
      try {
        await apiFetch(null, UPDATE_LINTER_CONFIG_DOCS_URL, {
          method: 'POST',
          body: updatedJson,
        });
        if (popupClose) {
          popupClose();
        }
        onLinterUpdate();
      } catch (error) {
        defaultApiFetchErrorHandler(error as any, { handleHttp: true });
      }
    }
  };
  return (
    <div>
      <div
        className={`${classes.roundBorderBox} ${classes.boxShadow}`}
        style={{ marginBottom: '20px' }}
      >
        <h3>Step Name:</h3>
        <select
          multiple={false}
          onChange={e => onStepNameSelect(e.target.value)}
          value={newLinterConfig.stepName}
          className={classes.select}
        >
          {lidarStepNames.map((step, i) => (
            <option key={i}>{step}</option>
          ))}
        </select>
      </div>
      <div className={`${classes.tripleColumnWrapper}`} style={{ width: 1668 }}>
        <TextInput
          label="Linter Name"
          value={newLinterConfig.name}
          onChange={e => setLinterName(e.target.value)}
          size="large"
          fullWidth
          className={`${classes.boxShadow} ${classes.roundBorder}`}
        />
      </div>
      <div style={{ marginTop: '20px' }}>
        <TextInput
          label="Description (What this linter does)"
          value={newLinterConfig.description}
          onChange={e => newSetValue('description', e.target.value)}
          multiline={true}
          size="large"
          fullWidth
          rows={3}
          className={`${classes.boxShadow} ${classes.roundBorder}`}
        />
      </div>
      <div style={{ marginTop: '20px' }}>
        <TextInput
          label="Technical Description (How this linter works)"
          value={newLinterConfig.technicalDescription ?? ''}
          onChange={e => newSetValue('technicalDescription', e.target.value)}
          multiline={true}
          size="large"
          fullWidth
          rows={3}
          className={`${classes.boxShadow} ${classes.roundBorder}`}
        />
      </div>
      <div>
        {newLinterConfig.stepName && (
          <>
            <div
              className={`${classes.roundBorderBox} ${classes.boxShadow}`}
              style={{ width: 380 }}
            >
              <h3>Evaluation Context:</h3>
              <select
                className={classes.select}
                multiple={false}
                onChange={e => newSetValue('context', e.target.value)}
                value={newLinterConfig.context}
              >
                {evaluationContexts.map((context, i) => (
                  <option key={i}>{context}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '40px', marginTop: '40px' }}>
              <LinterIO input={linterInput} output={linterOutput} />
            </div>
            <div className={`${classes.roundBorderBox} ${classes.boxShadow}`}>
              <h3>Step Arguments</h3>
              {!newLinterConfig.arguments.length ? (
                <div>No Step-Arguments required for this stepFn</div>
              ) : (
                <>
                  <div>Complete the stepFn arguments (ignore the step-name)</div>
                  <NewLinterArguments newSetValue={newSetValue} newLinterConfig={newLinterConfig} />
                </>
              )}
            </div>
          </>
        )}
      </div>
      <div className={`${classes.roundBorderBox} ${classes.boxShadow}`}>
        <h3>Config Fields:</h3>
        <div>
          Complete the required input fields for this linter (step arguments, qualifiers, assert...)
          <br></br>
          <br></br>
          <b>Name:</b> Config Name (e.g <b>label</b>)<br></br>
          <br></br>
          <b>Path:</b> The Path of the field that would be replaced by the Linter Assistant (e.g{' '}
          <b>qualifiers[0].label</b>)<br></br>
          <br></br>
          <b>Type:</b> The field type (e.g <b>string</b>)<br></br>
          <br></br>
          <b>Description:</b> What this config field does (e.g{' '}
          <b>Indicates the cuboid-label to lint</b>)<br></br>
          <br></br>
        </div>
        {newLinterConfig.config &&
          map(newLinterConfig.config, (config, fieldName) => (
            <div key={fieldName} style={{ padding: '10px 0px' }}>
              <div className={classes.tripleColumnWrapper} style={{ height: 60 }}>
                <TextInput
                  className={classes.tripleColumn}
                  label="Config Name"
                  value={fieldName}
                  onChange={e =>
                    editConfigName(
                      fieldName as LidarLinterConfigFields,
                      e.target.value as LidarLinterConfigFields,
                    )
                  }
                  size="large"
                  fullWidth
                />
                <TextInput
                  className={classes.tripleColumn}
                  label="Path"
                  value={config.path}
                  onChange={e => newSetValue(`config.${fieldName}.path`, e.target.value)}
                  size="large"
                  fullWidth
                />
                <select
                  className={classes.select}
                  multiple={false}
                  onChange={e => newSetValue(`config.${fieldName}.type`, e.target.value)}
                  value={config.type}
                  style={{ margin: 10, height: 40 }}
                >
                  {lidarLinterConfigTypes.map((configType, i) => (
                    <option key={i}>{configType}</option>
                  ))}
                </select>
              </div>
              <div className={classes.doubleColumnWrapper}>
                <div className={classes.widerColumn}>
                  <TextInput
                    label="Description"
                    value={config.description}
                    onChange={e => newSetValue(`config.${fieldName}.description`, e.target.value)}
                    size="large"
                    fullWidth
                  />
                </div>
                <div className={classes.thinnerColumn}>
                  <Button type="error" onClick={() => deleteConfigArg(fieldName)}>
                    <FontAwesomeIcon icon="trash" style={{ marginRight: 5 }} /> Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        <div>
          <Button type="primary" onClick={addNewConfigArg} style={{ margin: 5 }}>
            <FontAwesomeIcon icon="plus" style={{ marginRight: 5 }} /> Add a Linter Config Field
          </Button>
        </div>
      </div>
      {!(newLinterConfig.stepName && newLinterConfig.name.length) ? (
        ``
      ) : (
        <>
          <div>
            <h3>Examples</h3>
            <div>
              <b>Complete an example</b> (it will be used as a template by the Linter Assistant)
              <br></br>
              <br></br>
            </div>
            <form>
              {newLinterConfig.examples.map((exampleObject, index) => (
                <div key={`example${index}`}>
                  <div className={classes.doubleColumnWrapper}>
                    <div className={classes.widerColumn}>
                      <TextInput
                        label="Example Name"
                        value={exampleObject.name}
                        onChange={e => newSetValue(`examples[${index}].name`, e.target.value)}
                        size="large"
                        fullWidth
                      />
                    </div>
                    <div className={classes.thinnerColumn}>
                      <Button type="error" onClick={() => deleteConfigExample(index)}>
                        <FontAwesomeIcon icon="trash" style={{ marginRight: 5 }} /> Remove
                      </Button>
                    </div>
                  </div>
                  <div className={classes.tripleColumn}>
                    <h4>Example JSON</h4>
                    <JSONEditable
                      onEditJSON={e => newSetValue(`examples[${index}].configJson`, e)}
                      isShowingRaw={true}
                      json={exampleObject.configJson}
                    />
                  </div>
                </div>
              ))}
            </form>
          </div>
          <div className={classes.tripleColumn}>
            <Button type="primary" onClick={addNewConfigExample}>
              <FontAwesomeIcon icon="plus" style={{ marginRight: 5 }} /> Add an Example
            </Button>
          </div>
          <div>
            <h3>Preview</h3>
            <LinterLidarConfig
              linter={newLinterConfig}
              linterDocsJson={linterDocsJson}
              onLinterUpdate={onLinterUpdate}
              isPreview={true}
              selected={false}
              getStyle={(selected: boolean) =>
                selected ? { background: '#e8e8e8' } : { background: 'transparent' }
              }
            />
          </div>
          <Button type="primary" onClick={saveChanges}>
            <FontAwesomeIcon icon="save" style={{ marginRight: 5 }} /> Save
          </Button>
        </>
      )}
    </div>
  );
};

const LinterWizardPopover = (props: { currentLinter: LidarLinterSection }) => {
  const classes = styles();
  const { currentLinter } = props;
  return (
    <PopupState variant="popover" popupId="use-linter-wizard">
      {popupState => (
        <div style={{ padding: '0px 6px 0px 8px' }}>
          <Button className={classes.popoverButton} type="primary" {...bindTrigger(popupState)}>
            Use Linter Assistant <FontAwesomeIcon icon="wand-magic" style={{ marginLeft: 10 }} />
          </Button>
          <MuiPopover
            className={classes.popoverAdd}
            {...bindPopover(popupState)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            marginThreshold={48}
          >
            <Box sx={{ width: 900, padding: 40 }} p={0}>
              <LinterWizard currentLinter={{ ...currentLinter }} />
            </Box>
          </MuiPopover>
        </div>
      )}
    </PopupState>
  );
};

const StringArrayInput = (props: {
  newSetValue: (path: string, value: any) => void;
  path: string;
}) => {
  const { newSetValue, path } = props;
  const [values, setValues] = useState<string[]>(['']);

  useEffect(() => {
    newSetValue(path, values);
  }, [values]);

  const handleChange = (value: string, ix: number) => {
    const rawValues = [...values];
    rawValues[ix] = value;
    setValues(rawValues);
  };

  const deleteValue = (ix: number) => {
    const rawValues = [...values];
    rawValues.splice(ix, 1);
    setValues(rawValues);
  };

  return (
    <div
      style={{
        border: '1px solid #CCCCCC',
        borderRadius: '10px',
        padding: 12,
        backgroundColor: '#fafaff',
      }}
    >
      {values.length &&
        values.map((inputValue, ix) => (
          <div key={ix}>
            <TextInput
              onChange={e => handleChange(e.target.value, ix)}
              size={'medium'}
              // label={''}
              value={inputValue}
              style={{ width: 260, marginBottom: 10 }}
            />
            {values.length > 1 && (
              <Button
                type="error"
                onClick={e => {
                  e.stopPropagation();
                  deleteValue(ix);
                }}
                style={{ marginLeft: 20 }}
              >
                <FontAwesomeIcon icon="trash" style={{ marginRight: 5 }} />
                Remove
              </Button>
            )}
          </div>
        ))}
      <Button
        type="primary"
        onClick={e => {
          e.stopPropagation();
          setValues([...values, '']);
        }}
      >
        <FontAwesomeIcon icon="plus" style={{ marginRight: 5 }} />
        add a new element
      </Button>
    </div>
  );
};

const BooleanInput = (props: { newSetValue: (path: string, value: any) => void; path: string }) => {
  const { newSetValue, path } = props;
  const classes = styles();
  const booleanValues = ['true', 'false'];
  const handleChange = (value: string) => {
    newSetValue(path, value === 'true');
  };
  return (
    <div>
      <select
        className={classes.select}
        multiple={false}
        onChange={e => handleChange(e.target.value)}
      >
        {booleanValues.map((booleanValue, i) => (
          <option key={i}>{booleanValue}</option>
        ))}
      </select>
    </div>
  );
};

const NumberInput = (props: { newSetValue: (path: string, value: any) => void; path: string }) => {
  const { newSetValue, path } = props;
  return (
    <div style={{ display: 'flex' }}>
      <NumericInput
        value={0}
        onChange={inputValue => newSetValue(path, inputValue)}
        size={'sm'}
        style={{ height: 34, marginRight: 10 }}
      />
      <Button
        type="primary"
        // NumericInput onChange works once the focus is no longer on it
        // This button makes the user change the focus
      >
        Confirm Number
      </Button>
    </div>
  );
};

const getWizardInput = (
  configType: LidarLinterConfigTypes,
  newSetValue: (path: string, value: any) => void,
  path: string,
) => {
  switch (configType) {
    case LidarLinterConfigTypes.String:
      return (
        <TextInput
          onChange={e => newSetValue(path, e.target.value)}
          size={'medium'}
          style={{ width: 260 }}
        />
      );
    case LidarLinterConfigTypes.Number:
      return <NumberInput path={path} newSetValue={newSetValue} />;
    case LidarLinterConfigTypes.StringArray:
      return <StringArrayInput path={path} newSetValue={newSetValue} />;
    case LidarLinterConfigTypes.Boolean:
      return <BooleanInput path={path} newSetValue={newSetValue} />;
    default:
      return (
        <TextInput
          onChange={e => newSetValue(path, e.target.value)}
          size={'medium'}
          style={{ width: 260 }}
        />
      );
  }
};

const LinterWizard = (props: { currentLinter: LidarLinterSection }) => {
  const { currentLinter } = props;
  const [linter, setLinter] = useState(currentLinter.examples[0].configJson);
  const [isShowingRaw, toggleIsShowingRaw] = useToggle(false);

  const newSetValue = (path: string, newValue: any): void => {
    let configCopy = { ...linter };
    configCopy = set(configCopy, path, newValue);
    setLinter(configCopy);
  };

  return (
    <div>
      <h2>Complete the following fields to create your linter</h2>
      {currentLinter.config &&
        map(currentLinter.config, (configField, fieldName) => (
          <div style={{ marginTop: 20, marginBottom: 20 }} key={fieldName}>
            <b>
              {fieldName} - {configField.description}
            </b>{' '}
            ({configField.type})<br></br>
            {getWizardInput(
              configField.type as LidarLinterConfigTypes,
              newSetValue,
              configField.path,
            )}
          </div>
        ))}
      {isShowingRaw ? (
        <div
          style={{
            height: '100%',
            flex: 1,
            width: '100%',
          }}
        >
          <TextInput
            multiline={true}
            defaultValue={JSON.stringify(linter, null, 4)}
            style={{ width: '100%' }}
          />
        </div>
      ) : (
        <LinterJsonViewer
          src={linter}
          name={false}
          theme="monokai"
          iconStyle="triangle"
          collapsed={10}
          collapseStringsAfterLength={60}
          groupArraysAfterLength={false}
          displayObjectSize={false}
          displayDataTypes={false}
          style={{ borderRadius: '5px', padding: '10px' }}
        />
      )}
      <Button
        type="secondary"
        onClick={toggleIsShowingRaw}
        style={{ marginLeft: '10px', marginTop: '20px', marginBottom: '20px' }}
      >
        Toggle to {!isShowingRaw ? 'raw' : 'interactive'}
      </Button>
      <br></br>
      <Link href={'/lidarlinting'} target={'_blank'} style={{ marginLeft: 10 }}>
        Open Lidar Linting Tool
      </Link>
    </div>
  );
};
